<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import cloneDeep from 'lodash/cloneDeep';
  import { vueData } from '@/VueData';
  import { Globals } from '@/modules/Globals';
  import { IBranch } from '@/interfaces/IBranch';
  import { ICurrency } from '@/interfaces/ICurrency';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { IItem } from '@/interfaces/IItem';
  import { LabelPrinter } from '@/lib/print/LabelPrinter';
  import StickerProductsModal from '@/components/modals/sticker/StickerProductsModal.vue';
  import StickerPrintModal from '@/components/modals/sticker/StickerPrintModal.vue';
  import { ICategory } from '@/interfaces/ICategory';
  import { OptionType } from '@/enums/OptionType';
  import StickerSettingsModal from '@/components/modals/sticker/StickerSettingsModal.vue';
  import { AlertType } from '@/interfaces/IAlert';
  import { usePrinting } from '@/composition/use-printing';
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';

  const props = defineProps<{
    restaurant: IRestaurant;
    branch: IBranch;
    allBranches: IBranch[];
    branches: IBranch[];
    currency: ICurrency;
  }>();

  const menuStore = useMenuStore();
  const modals = useModal();
  const printing = usePrinting();

  const uploadsPath = Globals.uploadsPath;
  const loading = ref(false);
  const labelNumber = ref(0);

  onMounted(() => {
    loadLabelNumber();
  });

  const loadLabelNumber = () => {
    labelNumber.value = LabelPrinter.loadLabelNumber();
  };

  // Filter categoryBlocks to only show items with labelTemplate
  const filteredCategoryBlocks = computed(() => {
    if (!menuStore.categoryBlocks) return [];

    return menuStore.categoryBlocks
      .map((block) => {
        // Create a new block object with filtered items
        return {
          ...block,
          items: block.items
            .filter(
              (item) =>
                item.labelTemplate !== null &&
                item.labelTemplate !== undefined &&
                item.visible
            )
            .map((item) => {
              // Enrich with priceVariants settings for consistent behavior.
              // A deep copy: the print widget writes into the options, and
              // the menu store's items must stay as the server sent them.
              const enrichedItem = cloneDeep(item);

              /* if (enrichedItem.priceVariants) {
                enrichedItem.priceVariants.main = true;
                enrichedItem.priceVariants.max = 1;
                enrichedItem.priceVariants.type = OptionType.Choose;
                for (const option of enrichedItem.priceVariants.optionItems) {
                  option.active = true;
                  option.optionId = enrichedItem.priceVariants.id;
                }
              } */
              const priceVariants = enrichedItem.options;
              const options = enrichedItem.itemOptions;

              // @ts-ignore
              enrichedItem.priceVariants = priceVariants[0];
              // @ts-ignore
              enrichedItem.options = options.map((iO) => {
                const o = { ...iO.option };
                o.mandatory = iO.mandatory ?? o.mandatory;
                o.multiple = iO.multiple ?? o.multiple;
                o.min = iO.min ?? o.min;
                o.max = iO.max ?? o.max;
                o.active = iO.active ?? o.active;
                return o;
              });

              delete enrichedItem.itemOptions;

              return enrichedItem;
            })
            .sort((a, b) => a.index - b.index),
        };
      })
      .filter((block) => block.items.length > 0); // Only keep blocks with items
  });

  const print = async () => {
    await printing.printZpl(
      printing.stickerPrinter.value,
      '^XA^FO50,50^ADN,36,20^FDZebra Printer^FS^XZ'
    );
  };

  const selectItem = async (item: IItem) => {
    modals.show(
      StickerPrintModal,
      {
        item,
      },
      {
        name: 'sticker-print',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 640,
        clickToClose: false,
        transition: 'none',
      },
      {
        printed: async () => {
          labelNumber.value++;
        },
      }
    );
  };

  const resetCounter = () => {
    LabelPrinter.saveLabelNumber(0);
    loadLabelNumber();
  };

  const setupProducts = async () => {
    modals.show(
      StickerProductsModal,
      {
        restaurant: props.restaurant,
      },
      {
        name: 'sticker-products',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 980,
        clickToClose: false,
      },
      {
        submit: async () => {
          modals.hide('sticker-products');
        },
      }
    );
  };

  const setupSticker = async () => {
    modals.show(
      StickerSettingsModal,
      {},
      {
        name: 'sticker-settings',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 640,
        clickToClose: false,
      },
      {}
    );
  };
</script>

<template>
  <v-layout class="">
    <v-container fluid class="sticker-manager">
      <div class="main-navs-container">
        <v-card class="elevation-0 settings-nav">
          <v-card-text>
            <div class="counter-container">
              <span>Contador etiquetas</span>
              <span class="label-counter">#{{ labelNumber }}</span>
              <v-btn @click="resetCounter" plain small rounded color="black"
                ><v-icon small class="mr-2">refresh</v-icon> Resetear</v-btn
              >
            </div>
          </v-card-text>
        </v-card>
        <v-card class="elevation-0 settings-nav">
          <v-card-text>
            <div class="tw-flex tw-items-center tw-justify-end">
              <!-- <v-btn plain color="black"
                ><v-icon small class="mr-2">refresh</v-icon> Actualizar
              </v-btn> -->
              <v-btn @click="setupSticker" plain color="black"
                ><v-icon small class="mr-2">print</v-icon> etiqueta</v-btn
              >
              <v-btn @click="setupProducts" plain color="black"
                ><v-icon small class="mr-2">settings</v-icon> productos</v-btn
              >
            </div>
          </v-card-text>
        </v-card>
      </div>
      <v-card class="elevation-0">
        <v-card-title> Etiquetas </v-card-title>
        <v-card-subtitle>
          Este modulo le permite imprimir etiquetas adhesivas, si tiene
          impresoras Zebra o compatibles
        </v-card-subtitle>
        <v-card-text>
          <template v-if="!filteredCategoryBlocks.length || loading">
            <v-skeleton-loader type="sentences" />
          </template>
          <template v-else>
            <div class="groups">
              <div
                class="group"
                v-for="block of filteredCategoryBlocks"
                :key="block.id"
              >
                <div class="group-title">
                  {{ block.name }}
                </div>
                <div class="items">
                  <div
                    class="item"
                    v-for="item in block.items"
                    :key="item.id"
                    @click="selectItem(item)"
                  >
                    <div class="image">
                      <v-img
                        class="picture"
                        :src="`${uploadsPath}/${item.picture}`"
                      ></v-img>
                    </div>
                    <div class="meta">
                      {{ item.name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<style scoped lang="scss">
  .sticker-manager {
    @apply tw-space-y-4;
  }

  .print-nav {
    @apply -tw-mx-3 -tw-mt-3 tw-px-2 tw-py-1;
    background-color: #666666;

    .inner {
      @apply tw-flex tw-items-center tw-justify-end;
    }
  }

  .main-navs-container {
    @apply tw-flex tw-items-stretch tw-justify-between tw-gap-4;
    .settings-nav {
      @apply tw-flex tw-items-center;

      ::v-deep .v-card__text {
        @apply tw-p-2;
      }

      .counter-container {
        @apply tw-flex tw-items-center tw-justify-end tw-space-x-2;
        .label-counter {
          @apply tw-text-xl tw-pl-2;
        }
      }
    }
  }

  .groups {
    @apply tw-space-y-2;

    .group {
      .group-title {
        @apply tw-text-neutral-400 tw-font-medium;
      }

      .items {
        @apply tw-flex tw-gap-2 tw-flex-wrap;

        .item {
          @apply tw-h-20;
          @apply tw-px-2 tw-py-2 tw-border-b tw-flex tw-space-x-3 tw-items-center tw-bg-gray-50 tw-rounded-lg tw-shadow-sm;
          @apply tw-cursor-pointer;
          width: calc(50% - 0.3rem);

          @screen sm {
            width: calc(33% - 0.3rem);
          }

          @screen xl {
            width: calc(25% - 0.5rem);
          }

          .image {
            @apply tw-h-16 tw-w-16 tw-rounded-md tw-bg-gray-200;

            .picture {
              @apply tw-h-full tw-w-full;
            }
          }

          .meta {
          }
        }
      }
    }
  }
</style>
